import React, { Component } from "react";
import { connect } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import produce from "immer";
import { withStyles } from "@material-ui/core/styles";

// Other
import { locationPlotOptions } from "../../constants/highcharts/locationPlotOptions";
import { decimalPoints } from "../../constants/constants";
import { visibleIcon, notVisibleIcon } from "../../constants/customIcons";

const styles = {
  root: {},
};

class LocationPlot extends Component {
  constructor(props) {
    super(props);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.state = {
      windowHeight: 100,
      windowWidth: 100,
      visibilitySet: false,
    };
  }
  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  componentDidUpdate(prevProps) {
    // Is the user changes plot series visibility, then do no overwrite the visibility
    if (
      (prevProps.seriesVisibility !== this.props.seriesVisibility) &
      this.state.visibilitySet
    ) {
      this.setState({ ...this.state, visibilitySet: false });
    } else if (
      (prevProps.seriesVisibility === this.props.seriesVisibility) &
      !this.state.visibilitySet
    ) {
      this.setState({ ...this.state, visibilitySet: true });
    }
  }

  updateWindowDimensions() {
    //Set the plot graph size dynamically based on window size
    let newWidth = window.innerWidth * 0.9;
    let newHeight = newWidth * 0.4;
    this.setState({
      windowHeight: newHeight,
      windowWidth: newWidth,
    });
  }

  render() {
    const { classes } = this.props;
    // Update chart with values from redux store

    const chartOptions = produce(
      { ...locationPlotOptions(this.props.theme) },
      (draft) => {
        draft.chart.height = this.state.windowHeight; // resize chart based on screen size (rpi screen vs surface pro)
        draft.chart.width = this.state.windowWidth; // resize chart based on screen size (rpi screen vs surface pro)

        // Data
        let categories = [];
        let plotData = [];

        // Filter studies based on protocol and study preferences of the user
        let allProtocolStudyIds = this.props.allStudyIds.filter(
          (studyId) =>
            this.props.studyToggle[studyId] &&
            this.props.studies[studyId].studyInfo.protocolId ===
              this.props.protocolId
        );

        // Sort studies from newest to oldest
        allProtocolStudyIds.sort((a, b) => {
          return (
            Number(
              this.props.studies[a].studyInfo.studyDate.replace(/\//g, "")
            ) -
            Number(this.props.studies[b].studyInfo.studyDate.replace(/\//g, ""))
          );
        });

        allProtocolStudyIds.map((studyId) => {
          // Add study date to label
          categories.push(this.props.studies[studyId].studyInfo.studyDate);

          for (let locId of this.props.studies[studyId].locations.allIds) {
            let newValue = null;
            if (
              this.props.studies[studyId].locations.byId[locId].shearWaveSpeed
                .value !== null &&
              !this.props.studies[studyId].protocol.locations[locId].removed
            ) {
              newValue = Number(
                this.props.studies[studyId].locations.byId[
                  locId
                ].shearWaveSpeed.value.toFixed(decimalPoints)
              );
            }

            if (
              plotData.filter((location) => location.id === locId).length === 0
            ) {
              let newData = new Array(allProtocolStudyIds.length - 1).fill(
                null
              );
              newData[allProtocolStudyIds.indexOf(studyId)] = newValue;
              plotData.push({
                name: this.props.studies[studyId].locations.byId[locId].name,
                data: newData,
                id: locId,
                lineWidth: 2,
              });
            } else {
              for (let locData of plotData) {
                if (locData.id === locId) {
                  //Skip removed locations
                  locData.data[allProtocolStudyIds.indexOf(studyId)] = newValue;
                }
              }
            }
          }
        });

        let studies = this.props.studies;

        draft.title.text = this.props.studies[
          allProtocolStudyIds[0]
        ].studyInfo.protocolName;

        //SET VISIBILITY
        if (!this.state.visibilitySet && this.props.seriesVisibility !== null) {
          let seriesVisibility = this.props.seriesVisibility;
          plotData.forEach(function (series, index) {
            if (series.id !== seriesVisibility) {
              plotData[index]["visible"] = false;
            }
          });
        }
        draft.series = plotData;
        draft.xAxis.categories = categories;

        // TOOLTIP FORMATTER
        draft.tooltip.formatter = function () {
          let studyId = allProtocolStudyIds[this.point.x];
          let location =
            studies[studyId].locations.byId[this.point.series.userOptions.id];
          let thickness =
            location.thickness.value === null
              ? null
              : location.thickness.value.toFixed(decimalPoints);
          let alertState = location.alert.status;
          let notes =
            studies[studyId].protocol.locations[
              this.point.series.userOptions.id
            ].notes;
          let header = `<span style="color:${this.point.color}">●</span><span style="font-size: 12px">${this.point.series.name}, ${this.point.category}</span><br/><br/>`;
          let swsLine = `<span style="font-size: 12px"> SWS: <b>${this.point.y} m/s</b></span><br/>`;
          let thicknessLine = `<span style="font-size: 12px"> Thickness: <b>${thickness} mm</b></span><br/>`;
          let tooltip = header + swsLine + thicknessLine;

          if (notes !== null) {
            tooltip += `<span style="font-size: 12px"> Notes: <b>${notes}</b></span><br/>`;
          }
          if (alertState) {
            tooltip += `<span style="font-size: 12px"> Alert: <b>${alert}</b></span><br/>`;
          }
          return tooltip;
        };

        // LEGEND FROMATTER
        draft.legend.labelFormatter = function () {
          if (this.visible) {
            return `<span>${this.name} ${visibleIcon}</span>`;
          } else {
            return `<span>${this.name} ${notVisibleIcon}</span>`;
          }
        };
      }
    );

    return (
      <HighchartsReact
        className={classes.root}
        options={chartOptions}
        highcharts={Highcharts}
      />
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    allStudyIds: Object.keys(state.availableParticipants.reviewStudies),
    studies: state.availableParticipants.reviewStudies,
    studyToggle: ownProps.studyToggle,
    protocolId: ownProps.protocolId,
    seriesVisibility: ownProps.seriesVisibility,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(LocationPlot));
